import React, {
  useState, useEffect, useContext, createContext,
} from 'react';
import { useHistory } from 'react-router-dom';
import { Company, User } from 'Shared/util/types';
import { useUserContextQuery } from 'Shared/util/web-api-hooks';
import { getLink } from 'Shared/navigation/login/routes';
import { setSessionCookie, setSessionCookieHasExpired, getActiveCompanyId as getActiveCompanyCookie, setActiveCompanyId as setActiveCompanyCookie } from '../shared/util/user-session';


type UserContextModel = {
  user?: User;
  loading: boolean;
  loaded: boolean;
  updateUserSessionToken?: (token: string) => void;
  invalidateUserSession?: () => void;
  activeCompanyId: number;
  setActiveCompany?: (companyId: number, updatedUser?: User) => void;
  logOut?: () => void;
};

const UserContext = createContext<UserContextModel>({ loading: true, loaded: false, activeCompanyId: 0 });

const useUserContext = () => {
  const context = useContext(UserContext);
  if (context === undefined) throw new Error('useUserContext must be used within a UserProvider');
  return context;
};

type Props = {
  alwaysRender?: boolean;
};

export const UserProvider: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  alwaysRender
}) => {
  const currentActiveCompanyId = getActiveCompanyCookie();
  const [user, setUser] = useState<User | undefined>(undefined);
  const [activeCompanyId, setActiveCompanyId] = useState(currentActiveCompanyId);
  const [loaded, setLoaded] = useState(false);
  const [userContext, loadingUserContext, _, reloadUserContext] = useUserContextQuery();
  const history = useHistory();

  const setActiveCompany = (companyId: number, updatedUser?: User) => {
    setActiveCompanyCookie(companyId);
    setActiveCompanyId(companyId);

    const userToUpdate = updatedUser || user;
    if (userToUpdate !== undefined) {
      const activeCompany = userToUpdate.companies.find((x) => x.id === companyId);
      if (activeCompany) {
        setUser({ ...userToUpdate, activeCompany });
      }
      else {
        setUser(userToUpdate);
      }
    }
  };

  useEffect(() => {
    if (userContext) {
      const { user: loadedUser } = userContext;
      const activeCompany = (activeCompanyId && loadedUser.companies.find((x: Company) => x.id === activeCompanyId)) || loadedUser.defaultCompany;
      const updatedUser = { ...loadedUser, activeCompany };
      setUser(updatedUser);
      if (activeCompany) {
        setActiveCompany(activeCompany.id, updatedUser);
      }
      setLoaded(true);
    }
  }, [userContext, loadingUserContext]);

  const updateUserSessionToken = (token: string) => {
    setSessionCookie(token);
    reloadUserContext();
  };

  const invalidateUserSession = () => {
    setSessionCookieHasExpired();
    window.location.reload();
  };

  const logOut = () => {
    setActiveCompanyId(0);
    setSessionCookieHasExpired();
    history.push(getLink('login'));
  };

  const contextValues = {
    user,
    loading: loadingUserContext,
    loaded,
    updateUserSessionToken,
    invalidateUserSession,
    activeCompanyId,
    setActiveCompany,
    logOut
  };

  if (!loaded && !alwaysRender) {
    return null;
  }

  return (
    <UserContext.Provider value={contextValues}>
      {children}
    </UserContext.Provider>
  );
};

export default useUserContext;
